import type {
  ExtendedInitiative,
  FocusData,
  Initiative,
  KPIOverviewData,
} from '../../api/initiative-client/initiative-client.type';
import type { Board, ProjectsResponse } from '../../api/projects-client/projects-client.type';
import type { TransformedTarget } from '../../api/targets-client/targets-client.type';
import type { Epic, MinifiedTask } from '../../api/tasks-client/task-client.type';
import type {
  FlowByPhaseDataOverTime,
  MeasureDataResponse,
  MeasureMetadata,
} from '../../api/work-periods-client/work-periods-client.type';
import { CostMode } from '../../containers/strategy-v2/initiative-performance/cost-tab/cost-tab.type';
import { getInitialVisiblePhases } from '../../containers/strategy-v2/initiative-performance/flow-by-phase-tab/flow-by-phase-tab.helpers';
import type { Phase } from '../../containers/strategy-v2/initiative-performance/flow-by-phase-tab/flow-by-phase-tab.type';
import {
  EpicSortOption,
  FocusMode,
  PerformanceTab,
  SortDirection,
  ThroughputMode,
} from '../../containers/strategy-v2/initiative-performance/initiative-performance.type';
import { RelatedTasksMode } from '../../containers/strategy-v2/initiative-performance/related-tasks-tab/related-tasks-tab.type';
import { MeasureDataType } from '../../containers/strategy-v2/initiative-performance/targets-tab/targets-tab.type';
import { Metric } from '../../containers/strategy-v2/strategy-v2.type';
import {
  costTabInitialState,
  defaultFilters,
  flowByPhaseTabInitialState,
  focusTabInitialState,
  relatedTasksTabInitialState,
  targetsTabInitialState,
  throughputTabInitialState,
  useCostTabStore,
  useFlowByPhaseTabStore,
  useFocusTabStore,
  useRelatedTasksTabStore,
  useStrategyStore,
  useTargetsTabStore,
  useThroughputTabStore,
} from './strategy-store';
import type { FilterControls, Filters, Message } from './strategy-store.type';
import { Panel, ThroughputType, TreeMapMetric, View } from './strategy-store.type';

/**
 * Sets the initiatives in the Strategy store.
 *
 * @param {Initiative[]} initiatives - The initiatives to set
 * @return {void}
 */
const setInitiatives = (initiatives: Initiative[]): void => {
  useStrategyStore.setState({ initiatives }, false, '[Strategy Store] setInitiatives');
};

/**
 * Sets the KPI overview data in the Strategy store.
 *
 * @param {KPIOverviewData | null} kpiOverviewData - The KPI overview data to set
 * @return {void}
 */
const setKPIOverviewData = (kpiOverviewData: KPIOverviewData | null): void => {
  useStrategyStore.setState({ kpiOverviewData }, false, '[Strategy Store] setKPIOverviewData');
};

/**
 * Sets the view in the Strategy store.
 *
 * @param {View} view - The view to set
 * @return {void}
 */
const setView = (view: View): void => {
  useStrategyStore.setState({ view }, false, '[Strategy Store] setView');
};

/**
 * Sets the page in the Strategy store.
 *
 * @param {number} page - The page to set
 * @return {void}
 */
const setPage = (page: number): void => {
  useStrategyStore.setState({ page }, false, '[Strategy Store] setPage');
};

/**
 * Resets the page in the Strategy store to page 1 if the current page is greater than 1.
 *
 * @return {void}
 */
const resetPage = (): void => {
  const currentPage = useStrategyStore.getState().page;

  if (currentPage > 1) {
    setPage(1);
  }
};

/**
 * Sets the selected initiative in the Strategy store.
 *
 * @param {Initiative | null} initiative - The initiative to set, or null to clear
 * @return {void}
 */
const setSelectedInitiative = (initiative: Initiative | null): void => {
  useStrategyStore.setState({ selectedInitiative: initiative }, false, '[Strategy Store] setSelectedInitiative');

  if (initiative) {
    setActivePanel(Panel.Side);
  }
};

/**
 * Sets the initiative performance in the Strategy store.
 *
 * @param {ExtendedInitiative | null} initiativePerformance - The initiative performance to set, or null to clear
 * @return {void}
 */
const setInitiativePerformance = (initiativePerformance: ExtendedInitiative | null): void => {
  useStrategyStore.setState({ initiativePerformance }, false, '[Strategy Store] setInitiativePerformance');
};

/**
 * Sets the performance tab in the Strategy store.
 *
 * @param {PerformanceTab} performanceTab - The performance tab to set
 * @return {void}
 */
const setPerformanceTab = (performanceTab: PerformanceTab): void => {
  useStrategyStore.setState({ performanceTab }, false, '[Strategy Store] setPerformanceTab');
};

/**
 * Sets the active panel for the performance tab in the Strategy store.
 *
 * @param {Panel | null} panel - The panel to set, or null to clear
 * @return {void}
 */
const setPerformanceActivePanel = (panel: Panel | null): void => {
  useStrategyStore.setState({ performanceActivePanel: panel }, false, '[Strategy Store] setPerformanceActivePanel');
};

/**
 * Sets the message in the Strategy store.
 *
 * @param {Message | null} message - The message to set, or null to clear
 * @return {void}
 */
const setMessage = (message: Message | null): void => {
  useStrategyStore.setState({ message }, false, '[Strategy Store] setMessage');
};

/**
 * Sets the filters in the Strategy store.
 *
 * @param {Filters} filters - The filters to set
 * @return {void}
 */
const setFilters = (filters: Filters): void => {
  useStrategyStore.setState({ filters }, false, '[Strategy Store] setFilters');
  resetPage();
};

/**
 * Sets the status filters in the Strategy store.
 *
 * @param {string[]} statusFilters - The status filters to set
 * @return {void}
 */
const setStatusFilters = (statusFilters: string[]): void => {
  setFilters({ ...useStrategyStore.getState().filters, status: statusFilters });
};

/**
 * Sets the state filters in the Strategy store.
 *
 * @param {string[]} stateFilters - The state filters to set
 * @return {void}
 */
const setStateFilters = (stateFilters: string[]): void => {
  setFilters({ ...useStrategyStore.getState().filters, state: stateFilters });
};

/**
 * Sets the source filters in the Strategy store.
 *
 * @param {string[]} sourceFilters - The source filters to set
 * @return {void}
 */
const setSourceFilters = (sourceFilters: string[]): void => {
  setFilters({ ...useStrategyStore.getState().filters, source: sourceFilters });
};

/**
 * Sets the is_active filters in the Strategy store.
 *
 * @param {string[]} isActiveFilters - The is_active filters to set
 * @return {void}
 */
const setIsActiveFilters = (isActiveFilters: string[]): void => {
  setFilters({ ...useStrategyStore.getState().filters, is_active: isActiveFilters });
};

/**
 * Resets the filters in the Strategy store.
 *
 * @return {void}
 */
const resetFilters = (): void => {
  setFilters(defaultFilters);
};

/**
 * Sets the collapsed groups in the Strategy store.
 *
 * @param {Record<string, boolean>} collapsedGroups - The collapsed groups to set
 * @return {void}
 */
const setCollapsedGroups = (collapsedGroups: Record<string, boolean>): void => {
  useStrategyStore.setState({ collapsedGroups }, false, '[Strategy Store] setCollapsedState');
};

/**
 * Sets the collapsed group in the Strategy store.
 *
 * @param {string} group - The group to set
 * @param {boolean} collapsed - Whether the group is collapsed
 * @return {void}
 */
const setCollapsedGroup = (group: string, collapsed: boolean): void => {
  useStrategyStore.setState(
    { collapsedGroups: { ...useStrategyStore.getState().collapsedGroups, [group]: collapsed } },
    false,
    '[Strategy Store] setCollapsedGroup',
  );
};

/**
 * Toggles the collapsed state of a group in the Strategy store.
 *
 * @param {string} group - The group to toggle
 * @return {void}
 */
const toggleCollapsedGroup = (group: string): void => {
  useStrategyStore.setState(
    {
      collapsedGroups: {
        ...useStrategyStore.getState().collapsedGroups,
        [group]: !useStrategyStore.getState().collapsedGroups[group],
      },
    },
    false,
    '[Strategy Store] toggleCollapsedGroup',
  );
};

/**
 * Resets the collapsed groups in the Strategy store.
 *
 * @return {void}
 */
const resetCollapsedGroups = (): void => {
  useStrategyStore.setState(
    { collapsedGroups: { status: false, source: false } },
    false,
    '[Strategy Store] resetCollapsedGroups',
  );
};

/**
 * Sets the active panel in the Strategy store.
 *
 * @param {Panel | null} panel - The panel to set, or null to clear
 * @return {void}
 */
const setActivePanel = (panel: Panel | null): void => {
  useStrategyStore.setState({ activePanel: panel }, false, '[Strategy Store] setActivePanel');
};

/**
 * Sets the search term in the Strategy store.
 *
 * @param {string} searchTerm - The search term to set
 * @return {void}
 */
const setSearchTerm = (searchTerm: string): void => {
  useStrategyStore.setState({ searchTerm }, false, '[Strategy Store] setSearchTerm');
  resetPage();
};

/**
 * Sets the tree map metric in the Strategy store.
 *
 * @param {TreeMapMetric} treeMapMetric - The tree map metric to set
 * @return {void}
 */
const setTreeMapMetric = (treeMapMetric: TreeMapMetric): void => {
  useStrategyStore.setState({ treeMapMetric }, false, '[Strategy Store] setTreeMapMetric');
};

/**
 * Sets the epics in the Strategy store.
 *
 * @param {Epic[]} epics - The epics to set
 * @return {void}
 */
const setEpics = (epics: Epic[]): void => {
  useStrategyStore.setState({ epics }, false, '[Strategy Store] setEpics');
};

/**
 * Sets the mode for the throughput tab in the Strategy store.
 *
 * @param {ThroughputMode} throughputMode - The throughput mode to set
 * @return {void}
 */
const setThroughputMode = (throughputMode: ThroughputMode): void => {
  useThroughputTabStore.setState({ mode: throughputMode }, false, '[Strategy Store] setThroughputMode');
};

/**
 * Sets the metric for the throughput tab in the Strategy store.
 *
 * @param {Metric} metric - The metric to set
 * @return {void}
 */
const setThroughputMetric = (metric: Metric): void => {
  useThroughputTabStore.setState({ metric }, false, '[Strategy Store] setThroughputMetric');
};

/**
 * Sets the selected epics for the throughput tab in the Strategy store.
 *
 * @param {Epic[]} selectedEpics - The selected epics to set
 * @return {void}
 */
const setThroughputSelectedEpics = (selectedEpics: Epic[]): void => {
  useThroughputTabStore.setState({ selectedEpics }, false, '[Strategy Store] setSelectedEpics');
};

/**
 * Sets the selected teams for the throughput tab in the Strategy store.
 *
 * @param {ProjectsResponse[]} selectedTeams - The selected teams to set
 * @return {void}
 */
const setThroughputSelectedTeams = (selectedTeams: ProjectsResponse[]): void => {
  useThroughputTabStore.setState({ selectedTeams }, false, '[Strategy Store] setSelectedTeams');
};

/**
 * Sets the selected epic for the throughput tab in the Strategy store.
 *
 * @param {Epic | null} epic - The epic to set, or null to clear
 * @return {void}
 */
const setThroughputSelectedEpic = (epic: Epic | null): void => {
  useThroughputTabStore.setState({ selectedEpic: epic }, false, '[Strategy Store] setSelectedEpic');
};

/**
 * Sets the selected team for the throughput tab in the Strategy store.
 *
 * @param {ProjectsResponse | null} team - The team to set, or null to clear
 * @return {void}
 */
const setThroughputSelectedTeam = (team: ProjectsResponse | null): void => {
  useThroughputTabStore.setState({ selectedTeam: team }, false, '[Strategy Store] setSelectedTeam');
};

/**
 * Sets the selected date for the throughput tab in the Strategy store.
 *
 * @param {Date | null} date - The date to set, or null to clear
 * @return {void}
 */
const setThroughputSelectedDate = (date: Date | null): void => {
  useThroughputTabStore.setState({ selectedDate: date }, false, '[Strategy Store] setThroughputSelectedDate');
};

/**
 * Toggles the visibility of a throughput type in the throughput tab.
 *
 * @param {ThroughputType} type - The throughput type to toggle visibility for
 * @return {void}
 */
const toggleThroughputTypeVisibility = (type: ThroughputType): void => {
  const currentVisibleTypes = useThroughputTabStore.getState().visibleThroughputTypes;

  useThroughputTabStore.setState({
    visibleThroughputTypes: {
      ...currentVisibleTypes,
      [type]: !currentVisibleTypes[type],
    },
  });
};

/**
 * Sets the mode for the focus tab in the Strategy store.
 *
 * @param {FocusMode} focusMode - The focus mode to set
 * @return {void}
 */
const setFocusMode = (focusMode: FocusMode): void => {
  useFocusTabStore.setState({ mode: focusMode }, false, '[Strategy Store] setFocusMode');
};

/**
 * Sets the metric for the focus tab in the Strategy store.
 *
 * @param {Metric} metric - The metric to set
 * @return {void}
 */
const setFocusMetric = (metric: Metric): void => {
  useFocusTabStore.setState({ metric }, false, '[Strategy Store] setFocusMetric');
};

/**
 * Sets the selected teams for the focus tab in the Strategy store.
 *
 * @param {ProjectsResponse[]} selectedTeams - The selected teams to set
 * @return {void}
 */
const setFocusSelectedTeams = (selectedTeams: ProjectsResponse[]): void => {
  useFocusTabStore.setState({ selectedTeams }, false, '[Strategy Store] setSelectedTeams');
};

/**
 * Sets the selected boards for the focus tab in the Strategy store.
 *
 * @param {Board[]} selectedBoards - The selected boards to set
 * @return {void}
 */
const setFocusSelectedBoards = (selectedBoards: Board[]): void => {
  useFocusTabStore.setState({ selectedBoards }, false, '[Strategy Store] setSelectedBoards');
};

/**
 * Sets the focus teams data for the focus tab in the Strategy store.
 *
 * @param {FocusData | null} focusTeamsData - The focus teams data to set
 * @return {void}
 */
const setFocusTeamsData = (focusTeamsData: FocusData | null): void => {
  useFocusTabStore.setState({ focusTeamsData }, false, '[Strategy Store] setFocusTeamsData');
};

/**
 * Sets the focus boards data for the focus tab in the Strategy store.
 *
 * @param {FocusData | null} focusBoardsData - The focus boards data to set
 * @return {void}
 */
const setFocusBoardsData = (focusBoardsData: FocusData | null): void => {
  useFocusTabStore.setState({ focusBoardsData }, false, '[Strategy Store] setFocusBoardsData');
};

/**
 * Sets the metric for the Flow by Phase tab in the Strategy store.
 *
 * @param {Metric} metric - The metric to set
 * @return {void}
 */
const setFlowByPhaseMetric = (metric: Metric): void => {
  useFlowByPhaseTabStore.setState({ metric }, false, '[Strategy Store] setFlowByPhaseMetric');
};

/**
 * Sets the selected date for the Flow by Phase tab in the Strategy store.
 *
 * @param {string | null} selectedDate - The selected date to set, or null to clear
 * @return {void}
 */
const setFlowByPhaseSelectedDate = (selectedDate: string | null): void => {
  useFlowByPhaseTabStore.setState({ selectedDate }, false, '[Strategy Store] setFlowByPhaseSelectedDate');
};

/**
 * Sets the selected epic for the Flow by Phase tab in the Strategy store.
 *
 * @param {string | null} selectedEpic - The selected epic ID to set, or null to clear
 * @return {void}
 */
const setFlowByPhaseSelectedEpic = (selectedEpic: string | null): void => {
  useFlowByPhaseTabStore.setState({ selectedEpic }, false, '[Strategy Store] setFlowByPhaseSelectedEpic');
};

/**
 * Sets the Flow by Phase visible phases in the Strategy store.
 *
 * @param {string} phase - The phase to toggle visibility for
 * @return {void}
 */
const toggleFlowByPhasePhaseVisibility = (phase: string): void => {
  const currentVisiblePhases = useFlowByPhaseTabStore.getState().visiblePhases;
  useFlowByPhaseTabStore.setState(
    {
      visiblePhases: {
        ...currentVisiblePhases,
        [phase]: !currentVisiblePhases[phase],
      },
    },
    false,
    '[Strategy Store] toggleFlowByPhasePhaseVisibility',
  );
};

/**
 * Reset the Flow by Phase visible phases to show all phases.
 *
 * @return {void}
 */
const resetFlowByPhaseVisiblePhases = (): void => {
  useFlowByPhaseTabStore.setState(
    {
      visiblePhases: getInitialVisiblePhases(),
    },
    false,
    '[Strategy Store] resetFlowByPhaseVisiblePhases',
  );
};

/**
 * Sets the Flow by Phase data in the Strategy store.
 *
 * @param {FlowByPhaseDataOverTime | null} data - The Flow by Phase data to set
 * @return {void}
 */
const setFlowByPhaseData = (data: FlowByPhaseDataOverTime | null): void => {
  useFlowByPhaseTabStore.setState({ data }, false, '[Strategy Store] setFlowByPhaseData');
};

/**
 * Sets the Flow by Phase filter controls in the Strategy store.
 *
 * @param {FlowByPhaseFilterControls} filterControls - The filter controls to set
 * @return {void}
 */
const setFlowByPhaseFilterControls = (filterControls: FilterControls): void => {
  useFlowByPhaseTabStore.setState({ filterControls }, false, '[Strategy Store] setFlowByPhaseFilterControls');
};

/**
 * Sets the Flow by Phase filter search term in the Strategy store.
 *
 * @param {string} searchTerm - The search term to set
 * @return {void}
 */
const setFlowByPhaseFilterSearchTerm = (searchTerm: string): void => {
  setFlowByPhaseFilterControls({ ...useFlowByPhaseTabStore.getState().filterControls, searchTerm });
};

/**
 * Sets the Flow by Phase filter sort by in the Strategy store.
 *
 * @param {EpicSortOption} sortBy - The sort by to set
 * @return {void}
 */
const setFlowByPhaseFilterSortBy = (sortBy: EpicSortOption): void => {
  setFlowByPhaseFilterControls({ ...useFlowByPhaseTabStore.getState().filterControls, sortBy });
};

/**
 * Sets the Flow by Phase filter sort direction in the Strategy store.
 *
 * @param {SortDirection} sortDirection - The sort direction to set
 * @return {void}
 */
const setFlowByPhaseFilterSortDirection = (sortDirection: SortDirection): void => {
  setFlowByPhaseFilterControls({ ...useFlowByPhaseTabStore.getState().filterControls, sortDirection });
};

/**
 * Sets the Flow by Phase filter hide empty in the Strategy store.
 *
 * @param {boolean} hideEmpty - Whether to hide empty
 * @return {void}
 */
const setFlowByPhaseFilterHideEmpty = (hideEmpty: boolean): void => {
  setFlowByPhaseFilterControls({ ...useFlowByPhaseTabStore.getState().filterControls, hideEmpty });
};

/**
 * Sets the targets data in the Strategy store.
 *
 * @param {TransformedTarget[]} targets - The targets to set
 * @return {void}
 */
const setTargetsData = (targets: TransformedTarget[]): void => {
  useTargetsTabStore.setState({ targets }, false, '[Strategy Store] setTargetsData');
};

/**
 * Sets the selected target in the Strategy store.
 *
 * @param {TransformedTarget | null} selectedTarget - The selected target to set, or null to clear
 * @return {void}
 */
const setSelectedTarget = (selectedTarget: TransformedTarget | null): void => {
  useTargetsTabStore.setState({ selectedTarget }, false, '[Strategy Store] setSelectedTarget');
};

/**
 * Sets the available measures in the Strategy store.
 *
 * @param {MeasureMetadata[]} availableMeasures - The available measures to set
 * @return {void}
 */
const setAvailableMeasures = (availableMeasures: MeasureMetadata[]): void => {
  useTargetsTabStore.setState({ availableMeasures }, false, '[Strategy Store] setAvailableMeasures');
};

/**
 * Sets the measure data in the Strategy store.
 *
 * @param {MeasureDataResponse | null} measureData - The measure data to set
 * @return {void}
 */
const setMeasureData = (measureData: MeasureDataResponse | null): void => {
  useTargetsTabStore.setState({ measureData }, false, '[Strategy Store] setMeasureData');
};

/**
 * Sets the visible measures in the Strategy store.
 *
 * @param {Record<MeasureDataType, boolean>} visibleMeasures - The visible measures to set
 * @return {void}
 */
const setVisibleMeasures = (visibleMeasures: Record<MeasureDataType, boolean>): void => {
  useTargetsTabStore.setState({ visibleMeasures }, false, '[Strategy Store] setVisibleMeasures');
};

/**
 * Sets the cost mode in the Cost Tab store.
 *
 * @param {CostMode} costMode - The cost mode to set
 * @return {void}
 */
const setCostMode = (costMode: CostMode): void => {
  useCostTabStore.setState({ mode: costMode }, false, '[Strategy Store - Cost Tab] setCostMode');
};

/**
 * Sets the selected epics in the Cost Tab store.
 *
 * @param {Epic[]} selectedEpics - The selected epics to set
 * @return {void}
 */
const setCostSelectedEpics = (selectedEpics: Epic[]): void => {
  useCostTabStore.setState({ selectedEpics }, false, '[Strategy Store - Cost Tab] setSelectedEpics');
};

/**
 * Sets the selected teams in the Cost Tab store.
 *
 * @param {ProjectsResponse[]} selectedTeams - The selected teams to set
 * @return {void}
 */
const setCostSelectedTeams = (selectedTeams: ProjectsResponse[]): void => {
  useCostTabStore.setState({ selectedTeams }, false, '[Strategy Store - Cost Tab] setSelectedTeams');
};

/**
 * Sets the filter controls in the Cost Tab store.
 *
 * @param {FilterControls} filterControls - The filter controls to set
 * @return {void}
 */
const setCostFilterControls = (filterControls: FilterControls): void => {
  useCostTabStore.setState({ filterControls }, false, '[Strategy Store - Cost Tab] setFilterControls');
};

/**
 * Sets the search term in the Cost Tab store.
 *
 * @param {string} searchTerm - The search term to set
 * @return {void}
 */
const setCostFilterSearchTerm = (searchTerm: string): void => {
  setCostFilterControls({ ...useCostTabStore.getState().filterControls, searchTerm });
};

/**
 * Sets the sort by in the Cost Tab store.
 *
 * @param {EpicSortOption} sortBy - The sort by to set
 * @return {void}
 */
const setCostFilterSortBy = (sortBy: EpicSortOption): void => {
  setCostFilterControls({ ...useCostTabStore.getState().filterControls, sortBy });
};

/**
 * Sets the sort direction in the Cost Tab store.
 *
 * @param {SortDirection} sortDirection - The sort direction to set
 * @return {void}
 */
const setCostFilterSortDirection = (sortDirection: SortDirection): void => {
  setCostFilterControls({ ...useCostTabStore.getState().filterControls, sortDirection });
};

/**
 * Sets the hide empty in the Cost Tab store.
 *
 * @param {boolean} hideEmpty - Whether to hide empty
 * @return {void}
 */
const setCostFilterHideEmpty = (hideEmpty: boolean): void => {
  setCostFilterControls({ ...useCostTabStore.getState().filterControls, hideEmpty });
};

/**
 * Sets the selected epic in the Cost Tab store.
 *
 * @param {Epic | null} epic - The selected epic to set, or null to clear
 * @return {void}
 */
const setCostSelectedEpic = (epic: Epic | null): void => {
  useCostTabStore.setState({ selectedEpic: epic }, false, '[Strategy Store - Cost Tab] setSelectedEpic');
};

/**
 * Sets the selected team in the Cost Tab store.
 *
 * @param {ProjectsResponse | null} team - The selected team to set, or null to clear
 * @return {void}
 */
const setCostSelectedTeam = (team: ProjectsResponse | null): void => {
  useCostTabStore.setState({ selectedTeam: team }, false, '[Strategy Store - Cost Tab] setSelectedTeam');
};

/**
 * Sets the mode for the Related Tasks Tab store.
 *
 * @param {RelatedTasksMode} mode - The mode to set
 * @return {void}
 */
const setRelatedTasksMode = (mode: RelatedTasksMode): void => {
  useRelatedTasksTabStore.setState({ mode }, false, '[Strategy Store - Related Tasks Tab] setMode');
};

/**
 * Sets the selected epic for the Related Tasks Tab store.
 *
 * @param {string | null} selectedEpic - The selected epic to set, or null to clear
 * @return {void}
 */
const setRelatedTasksSelectedEpic = (selectedEpic: string | null): void => {
  useRelatedTasksTabStore.setState({ selectedEpic }, false, '[Strategy Store - Related Tasks Tab] setSelectedEpic');
};

/**
 * Sets the selected team for the Related Tasks Tab store.
 *
 * @param {string | null} selectedTeam - The selected team to set, or null to clear
 * @return {void}
 */
const setRelatedTasksSelectedTeam = (selectedTeam: string | null): void => {
  useRelatedTasksTabStore.setState({ selectedTeam }, false, '[Strategy Store - Related Tasks Tab] setSelectedTeam');
};

/**
 * Sets the selected date for the Related Tasks Tab store.
 *
 * @param {Date | null} selectedDate - The selected date to set, or null to clear
 * @return {void}
 */
const setRelatedTasksSelectedDate = (selectedDate: Date | null): void => {
  useRelatedTasksTabStore.setState({ selectedDate }, false, '[Strategy Store - Related Tasks Tab] setSelectedDate');
};

/**
 * Sets the search term for the Related Tasks Tab store.
 *
 * @param {string} searchTerm - The search term to set
 * @return {void}
 */
const setRelatedTasksSearchTerm = (searchTerm: string): void => {
  useRelatedTasksTabStore.setState({ searchTerm }, false, '[Strategy Store - Related Tasks Tab] setSearchTerm');
  resetRelatedTasksPage();
};

/**
 * Sets the page for the Related Tasks Tab store.
 *
 * @param {number} page - The page to set
 * @return {void}
 */
const setRelatedTasksPage = (page: number): void => {
  useRelatedTasksTabStore.setState({ page }, false, '[Strategy Store - Related Tasks Tab] setPage');
};

/**
 * Resets the page in the Related Tasks Tab store to page 1 if the current page is greater than 1.
 *
 * @return {void}
 */
const resetRelatedTasksPage = (): void => {
  const page = useRelatedTasksTabStore.getState().page;

  if (page > 1) {
    setRelatedTasksPage(1);
  }
};

/**
 * Sets the selected task for the Related Tasks Tab store.
 *
 * @param {MinifiedTask | null} selectedTask - The selected task to set, or null to clear
 * @return {void}
 */
const setRelatedTasksSelectedTask = (selectedTask: MinifiedTask | null): void => {
  useRelatedTasksTabStore.setState({ selectedTask }, false, '[Strategy Store - Related Tasks Tab] setSelectedTask');
};

/**
 * Sets the filter controls for the Related Tasks Tab store.
 *
 * @param {FilterControls} filterControls - The filter controls to set
 * @return {void}
 */
const setRelatedTasksFilterControls = (filterControls: FilterControls): void => {
  useRelatedTasksTabStore.setState({ filterControls }, false, '[Strategy Store - Related Tasks Tab] setFilterControls');
};

/**
 * Sets the search term for the filter controls of the Related Tasks Tab store.
 *
 * @param {string} searchTerm - The search term to set
 * @return {void}
 */
const setRelatedTasksFilterSearchTerm = (searchTerm: string): void => {
  setRelatedTasksFilterControls({ ...useRelatedTasksTabStore.getState().filterControls, searchTerm });
};

/**
 * Sets the sort by for the filter controls of the Related Tasks Tab store.
 *
 * @param {EpicSortOption} sortBy - The sort by to set
 * @return {void}
 */
const setRelatedTasksFilterSortBy = (sortBy: EpicSortOption): void => {
  setRelatedTasksFilterControls({ ...useRelatedTasksTabStore.getState().filterControls, sortBy });
};

/**
 * Sets the sort direction for the filter controls of the Related Tasks Tab store.
 *
 * @param {SortDirection} sortDirection - The sort direction to set
 * @return {void}
 */
const setRelatedTasksFilterSortDirection = (sortDirection: SortDirection): void => {
  setRelatedTasksFilterControls({ ...useRelatedTasksTabStore.getState().filterControls, sortDirection });
};

/**
 * Sets the hide empty for the filter controls of the Related Tasks Tab store.
 *
 * @param {boolean} hideEmpty - Whether to hide empty
 * @return {void}
 */
const setRelatedTasksFilterHideEmpty = (hideEmpty: boolean): void => {
  setRelatedTasksFilterControls({ ...useRelatedTasksTabStore.getState().filterControls, hideEmpty });
};

/**
 * Sets the selected throughput for the Related Tasks Tab store.
 *
 * @param {string} selectedThroughput - The selected throughput to set
 * @return {void}
 */
const setRelatedTasksSelectedThroughput = (selectedThroughput: 'total' | 'completed'): void => {
  useRelatedTasksTabStore.setState(
    { selectedThroughput },
    false,
    '[Strategy Store - Related Tasks Tab] setSelectedThroughput',
  );
  resetRelatedTasksPage();
};

/**
 * Sets the selected task types for the Related Tasks Tab store.
 *
 * @param {string[]} selectedTypes - Array of task type strings to set as selected
 * @return {void}
 */
const setRelatedTasksSelectedTypes = (selectedTypes: string[]): void => {
  useRelatedTasksTabStore.setState({ selectedTypes }, false, '[Strategy Store - Related Tasks Tab] setSelectedTypes');
  resetRelatedTasksPage();
};

/**
 * Sets the selected phase for the Related Tasks Tab store.
 *
 * @param {Phase | null} selectedPhase - The selected phase to set, or null to clear
 * @return {void}
 */
const setRelatedTasksSelectedPhase = (selectedPhase: Phase | null): void => {
  useRelatedTasksTabStore.setState({ selectedPhase }, false, '[Strategy Store - Related Tasks Tab] setSelectedPhase');
};

/**
 * Sets the tasks for the Related Tasks Tab store.
 *
 * @param {MinifiedTask[]} tasks - The tasks to set
 * @return {void}
 */
const setRelatedTasksData = (tasks: MinifiedTask[]): void => {
  useRelatedTasksTabStore.setState({ tasks }, false, '[Strategy Store - Related Tasks Tab] setTasks');
};

/**
 * Triggers a set of actions to show the related tasks in the Contributing Tasks mode.
 *
 * @param {Object} options - The options to set
 * @param {string} options.throughput - The throughput to set
 * @param {Date} options.date - The date to set
 * @return {void}
 */
const showRelatedTasks = ({ throughput, date }: { throughput?: 'total' | 'completed'; date?: Date }): void => {
  if (throughput) {
    setRelatedTasksSelectedThroughput(throughput);
  }

  if (date) {
    setRelatedTasksSelectedDate(date);
  }

  setRelatedTasksSelectedPhase(null);
  setRelatedTasksMode(RelatedTasksMode.ContributingTasks);
  setPerformanceActivePanel(Panel.Filters);
  setPerformanceTab(PerformanceTab.RelatedTasks);
};

/**
 * Triggers a set of actions to show the related tasks in the Contributing Epics mode.
 *
 * @param {Object} options - The options to set
 * @param {string} options.epic - The epic to set
 * @param {Phase} options.phase - The phase to set
 * @param {Date} options.date - The date to set
 * @return {void}
 */
const showEpicRelatedTasks = ({ epic, phase, date }: { epic?: string; phase?: Phase; date?: Date }): void => {
  if (epic) {
    setRelatedTasksSelectedEpic(epic);
  }

  if (date) {
    setRelatedTasksSelectedDate(date);
  }

  setRelatedTasksSelectedPhase(phase || null);
  setRelatedTasksMode(RelatedTasksMode.ContributingEpics);
  setPerformanceActivePanel(Panel.Filters);
  setPerformanceTab(PerformanceTab.RelatedTasks);
};

/**
 * Triggers a set of actions to show the related tasks in the Contributing Teams mode.
 *
 * @param {Object} options - The options to set
 * @param {string} options.team - The team to set
 * @param {Date} options.date - The date to set
 * @return {void}
 */
const showTeamRelatedTasks = ({ team, date }: { team?: string; date?: Date }): void => {
  if (team) {
    setRelatedTasksSelectedTeam(team);
  }

  if (date) {
    setRelatedTasksSelectedDate(date);
  }

  setRelatedTasksSelectedPhase(null);
  setRelatedTasksMode(RelatedTasksMode.ContributingTeams);
  setPerformanceActivePanel(Panel.Filters);
  setPerformanceTab(PerformanceTab.RelatedTasks);
};

/**
 * Resets the Throughput Tab store state to its initial values.
 *
 * @return {void}
 */
const resetThroughputTabState = (): void => {
  useThroughputTabStore.setState(
    { ...throughputTabInitialState },
    false,
    '[Strategy Store - Throughput Tab] resetState',
  );
};

/**
 * Resets the Focus Tab store state to its initial values.
 *
 * @return {void}
 */
const resetFocusTabState = (): void => {
  useFocusTabStore.setState({ ...focusTabInitialState }, false, '[Strategy Store - Focus Tab] resetState');
};

/**
 * Resets the Flow By Phase Tab store state to its initial values.
 *
 * @return {void}
 */
const resetFlowByPhaseTabState = (): void => {
  useFlowByPhaseTabStore.setState(
    { ...flowByPhaseTabInitialState },
    false,
    '[Strategy Store - Flow By Phase Tab] resetState',
  );
};

/**
 * Resets the Targets Tab store state to its initial values.
 *
 * @return {void}
 */
const resetTargetsTabState = (): void => {
  useTargetsTabStore.setState({ ...targetsTabInitialState }, false, '[Strategy Store - Targets Tab] resetState');
};

/**
 * Resets the Cost Tab store state to its initial values.
 *
 * @return {void}
 */
const resetCostTabState = (): void => {
  useCostTabStore.setState({ ...costTabInitialState }, false, '[Strategy Store - Cost Tab] resetState');
};

/**
 * Resets the Related Tasks Tab store state.
 *
 * @return {void}
 */
const resetRelatedTasksTabState = (): void => {
  useRelatedTasksTabStore.setState(
    { ...relatedTasksTabInitialState },
    false,
    '[Strategy Store - Related Tasks Tab] resetState',
  );
};

/**
 * Resets the Initiative Performance state to its initial values.
 *
 * @return {void}
 */
const resetInitiativePerformanceState = (): void => {
  setInitiativePerformance(null);
  resetThroughputTabState();
  resetFocusTabState();
  resetFlowByPhaseTabState();
  resetTargetsTabState();
  resetCostTabState();
  resetRelatedTasksTabState();
};

export {
  resetCollapsedGroups,
  resetCostTabState,
  resetFilters,
  resetFlowByPhaseTabState,
  resetFlowByPhaseVisiblePhases,
  resetFocusTabState,
  resetInitiativePerformanceState,
  resetPage,
  resetRelatedTasksTabState,
  resetTargetsTabState,
  resetThroughputTabState,
  setActivePanel,
  setAvailableMeasures,
  setCollapsedGroup,
  setCollapsedGroups,
  setCostFilterControls,
  setCostFilterHideEmpty,
  setCostFilterSearchTerm,
  setCostFilterSortBy,
  setCostFilterSortDirection,
  setCostMode,
  setCostSelectedEpic,
  setCostSelectedEpics,
  setCostSelectedTeam,
  setCostSelectedTeams,
  setEpics,
  setFilters,
  setFlowByPhaseData,
  setFlowByPhaseFilterControls,
  setFlowByPhaseFilterHideEmpty,
  setFlowByPhaseFilterSearchTerm,
  setFlowByPhaseFilterSortBy,
  setFlowByPhaseFilterSortDirection,
  setFlowByPhaseMetric,
  setFlowByPhaseSelectedDate,
  setFlowByPhaseSelectedEpic,
  setFocusBoardsData,
  setFocusMetric,
  setFocusMode,
  setFocusSelectedBoards,
  setFocusSelectedTeams,
  setFocusTeamsData,
  setInitiativePerformance,
  setInitiatives,
  setIsActiveFilters,
  setKPIOverviewData,
  setMeasureData,
  setMessage,
  setPage,
  setPerformanceActivePanel,
  setPerformanceTab,
  setRelatedTasksData,
  setRelatedTasksFilterHideEmpty,
  setRelatedTasksFilterSearchTerm,
  setRelatedTasksFilterSortBy,
  setRelatedTasksFilterSortDirection,
  setRelatedTasksMode,
  setRelatedTasksPage,
  setRelatedTasksSearchTerm,
  setRelatedTasksSelectedDate,
  setRelatedTasksSelectedEpic,
  setRelatedTasksSelectedPhase,
  setRelatedTasksSelectedTask,
  setRelatedTasksSelectedTeam,
  setRelatedTasksSelectedThroughput,
  setRelatedTasksSelectedTypes,
  setSearchTerm,
  setSelectedInitiative,
  setSelectedTarget,
  setSourceFilters,
  setStateFilters,
  setStatusFilters,
  setTargetsData,
  setThroughputMetric,
  setThroughputMode,
  setThroughputSelectedDate,
  setThroughputSelectedEpic,
  setThroughputSelectedEpics,
  setThroughputSelectedTeam,
  setThroughputSelectedTeams,
  setTreeMapMetric,
  setView,
  setVisibleMeasures,
  showEpicRelatedTasks,
  showRelatedTasks,
  showTeamRelatedTasks,
  toggleCollapsedGroup,
  toggleFlowByPhasePhaseVisibility,
  toggleThroughputTypeVisibility,
};
